import { useLocalStorage } from '@/hooks';
import { useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';

interface UsePhoneNumberSearchResult {
    phoneNumber: string | null;
    setPhoneNumber: (value: string | null) => void;
    clearPhoneNumber: () => void;
}

const PHONE_SEARCH_PARAM = 'phone';
const DEBOUNCE_TIMEOUT = 500;
const STORAGE_KEY = 'storedPhoneNumber';

/**
 * Custom hook to manage phone number search state synced with URL parameters and localStorage.
 *
 * URL parameter is used to enable browser back/forward navigation and
 * localStorage is used to persist the searched phoneNumber across page visits/refreshes,
 * so returning users see their last searched phoneNumber.
 *
 * URL parameters take precedence over localStorage when both exist.
 */
const usePhoneNumberSearch = (): UsePhoneNumberSearchResult => {
    const [searchParams, setSearchParams] = useSearchParams();
    const phoneNumberFromUrl = searchParams.get(PHONE_SEARCH_PARAM);

    const [storedPhoneNumber, setStoredPhoneNumber] = useLocalStorage<string | null>(STORAGE_KEY, null);

    // Determine the current phoneNumber (URL takes precedence over localStorage)
    const phoneNumber = phoneNumberFromUrl ?? storedPhoneNumber ?? '';

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInternalUpdateRef = useRef(false);

    // Only update localStorage when URL has a phoneNumber  (don't clear it when navigating away)
    useEffect(() => {
        if (phoneNumberFromUrl) {
            setStoredPhoneNumber(phoneNumberFromUrl);
        }
    }, [phoneNumberFromUrl, setStoredPhoneNumber]);

    const updateUrl = useCallback(
        (value: string | null) => {
            isInternalUpdateRef.current = true;
            timeoutRef.current = null;

            setSearchParams(
                (prev: URLSearchParams) => {
                    const newParams = new URLSearchParams(prev);

                    if (value) {
                        newParams.set(PHONE_SEARCH_PARAM, value);
                    } else {
                        newParams.delete(PHONE_SEARCH_PARAM);
                    }

                    newParams.delete('page');
                    return newParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setPhoneNumber = useCallback(
        (value: string | null) => {
            setStoredPhoneNumber(value || null);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                updateUrl(value);
            }, DEBOUNCE_TIMEOUT);
        },
        [updateUrl, setStoredPhoneNumber]
    );

    const clearPhoneNumber = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setStoredPhoneNumber(null);

        timeoutRef.current = null;

        updateUrl(null);
    }, [updateUrl]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { phoneNumber, setPhoneNumber, clearPhoneNumber };
};

export default usePhoneNumberSearch;
